import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, FileText, ClipboardCheck, CreditCard, Calendar, Users } from 'lucide-react';
import { motion } from 'framer-motion';

export default function AdmissionsPage() {
  const { language } = useLanguage();

  const processSteps = language === 'fr' ? [
    {
      icon: FileText,
      step: '01',
      title: 'Demande d\'admission',
      desc: 'Remplissez le formulaire de demande d\'admission sur OFA Family Connect, notre plateforme dédiée aux familles.',
    },
    {
      icon: ClipboardCheck,
      step: '02',
      title: 'Étude du dossier + Évaluation',
      desc: 'Notre équipe pédagogique étudie le dossier de votre enfant et évalue son niveau d\'anglais pour déterminer le parcours adapté.',
    },
    {
      icon: CreditCard,
      step: '03',
      title: 'Paiement + Inscription',
      desc: 'Une fois l\'admission confirmée, procédez au paiement et finalisez l\'inscription de votre enfant.',
    },
  ] : [
    {
      icon: FileText,
      step: '01',
      title: 'Admission Application',
      desc: 'Complete the admission application on OFA Family Connect, our dedicated family platform.',
    },
    {
      icon: ClipboardCheck,
      step: '02',
      title: 'Application Review + Assessment',
      desc: 'Our academic team reviews your child\'s application and assesses their English level to determine the appropriate track.',
    },
    {
      icon: CreditCard,
      step: '03',
      title: 'Payment + Enrollment',
      desc: 'Once admission is confirmed, proceed with payment and finalize your child\'s enrollment.',
    },
  ];

  return (
    <div data-testid="admissions-page" className="min-h-screen bg-white">
      <SEO path="/admissions" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#66CA9D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Admissions' : 'Admissions'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/80 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Rejoignez OFA France et offrez à votre enfant un parcours bilingue et international d\'excellence.'
              : 'Join OFA France and give your child an outstanding bilingual and international pathway.'}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Rejoindre OFA France est un processus simple en trois étapes. Que votre enfant soit déjà bilingue, de retour d\'expatriation, ou qu\'il souhaite développer son anglais, nous avons un parcours adapté. L\'inscription est ouverte toute l\'année, avec une rentrée principale en septembre et des possibilités d\'intégration en cours d\'année sous conditions.'
              : 'Joining OFA France is a simple three-step process. Whether your child is already bilingual, returning from expatriation, or wants to develop their English, we have an adapted track. Registration is open year-round, with a main start in September and mid-year entry options subject to conditions.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Après votre demande d\'admission, notre équipe pédagogique évalue le niveau d\'anglais de votre enfant afin de l\'orienter vers le parcours le plus adapté : l\'OFA Fluent Track (Rester Bilingue) ou l\'OFA Bridge Track (Devenir Bilingue). Des frais de dossier de 75 € sont demandés pour l\'admission. Notre objectif est de placer chaque élève dans l\'environnement qui lui permettra de progresser au mieux.'
              : 'After your admission request, our academic team assesses your child\'s English level to guide them to the most suitable track: the OFA Fluent Track (Stay Bilingual) or the OFA Bridge Track (Become Bilingual). A €75 application fee is required for admission. Our goal is to place each student in the environment that will allow them to progress best.'}
          </p>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-[#14274B] mb-12"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Processus d\'Admission' : 'Admission Process'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                data-testid={`process-step-${index}`}
                className="relative"
              >
                <div className="text-6xl font-light text-[#E7EAEF] mb-4" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {step.step}
                </div>
                <div className="w-12 h-12 bg-[#66CA9D]/10 flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-[#66CA9D]" />
                </div>
                <h3
                  className="text-xl font-semibold text-[#14274B] mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-[#475569]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OFA Family Connect */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-[#2D78F6]" />
                <h2
                  className="text-2xl md:text-3xl font-light text-[#14274B]"
                  style={{ fontFamily: 'Gabarito, sans-serif' }}
                >
                  OFA Family Connect
                </h2>
              </div>
              <p
                className="text-[#475569] leading-relaxed mb-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr'
                  ? 'Notre plateforme dédiée aux familles, connectée au site, qui vous permettra de gérer l\'ensemble du processus d\'admission : demande, suivi du dossier, évaluation et inscription.'
                  : 'Our dedicated family platform, connected to the website, that will allow you to manage the entire admission process: application, file tracking, assessment and enrollment.'}
              </p>
              <p
                className="text-[#2D78F6] font-medium text-sm"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr' ? 'Bientôt disponible' : 'Coming soon'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 border border-[#E7EAEF]"
            >
              <div className="flex items-center gap-4 mb-6">
                <Calendar className="w-6 h-6 text-[#66CA9D]" />
                <h3
                  className="text-xl font-semibold text-[#14274B]"
                  style={{ fontFamily: 'Gabarito, sans-serif' }}
                >
                  {language === 'fr' ? 'Calendrier 2026-2027' : 'Calendar 2026-2027'}
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-[#66CA9D]/5 border-l-4 border-[#66CA9D]">
                  <div>
                    <p className="font-semibold text-[#14274B]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {language === 'fr' ? 'Ouverture des inscriptions' : 'Enrollment opens'}
                    </p>
                    <p className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {language === 'fr' ? 'À partir du 15 juin' : 'From June 15th'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#2D78F6]/5 border-l-4 border-[#2D78F6]">
                  <div>
                    <p className="font-semibold text-[#14274B]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {language === 'fr' ? 'Rentrée scolaire' : 'School year begins'}
                    </p>
                    <p className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {language === 'fr' ? '14 septembre' : 'September 14th'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COGNIA Accreditation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-[#14274B] p-8 sm:p-12 flex flex-col sm:flex-row items-center gap-8"
            data-testid="admissions-cognia"
          >
            <div className="flex-shrink-0">
              <img
                src="/cognia_seal.png"
                alt="COGNIA — Accréditation programme OFA France"
                className="h-20 sm:h-24 w-auto"
              />
            </div>
            <div className="text-center sm:text-left">
              <h3
                className="text-xl sm:text-2xl font-semibold text-white mb-2"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {language === 'fr' ? 'Programme accrédité COGNIA' : 'COGNIA Accredited Program'}
              </h3>
              <p
                className="text-white/70 leading-relaxed mb-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr'
                  ? 'En rejoignant OFA France, votre enfant intègre un programme accrédité par COGNIA, garantissant les plus hauts standards académiques et un diplôme américain reconnu dans le monde entier par les universités.'
                  : 'By joining OFA France, your child enters a COGNIA-accredited program, guaranteeing the highest academic standards and an American diploma recognized worldwide by universities.'}
              </p>
              <div className="flex flex-wrap items-center gap-4 justify-center sm:justify-start">
                <span className="text-white/50 text-xs font-medium px-3 py-1 border border-white/20" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  40 000+ {language === 'fr' ? 'établissements' : 'institutions'}
                </span>
                <span className="text-white/50 text-xs font-medium px-3 py-1 border border-white/20" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  100+ {language === 'fr' ? 'pays' : 'countries'}
                </span>
                <span className="text-white/50 text-xs font-medium px-3 py-1 border border-white/20" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  17M+ {language === 'fr' ? 'élèves' : 'students'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Prêt à Construire l\'Avenir International de Votre Enfant ?' : 'Ready to Build Your Child\'s International Future?'}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="admissions-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr' ? 'S\'inscrire' : 'Register'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
